import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import MapBackground from './MapBackground';

// Asset types available in the system
const ASSET_TYPES = [
  { id: 'battery_sites', name: 'UPS & Battery Sites' },
  { id: 'sdh_sites', name: 'SDH Sites' },
  { id: 'pdh_sites', name: 'PDH Sites' },
  { id: 'telecoms_sites', name: 'Telecoms Sites' },
  { id: 'loc_sites', name: 'LOC Sites' },
  { id: 'spt_sites', name: 'Signals & Points' },
  { id: 'fibre_sites', name: 'Fibre Cables' }
];

// Styled components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: var(--app-height, 100vh);
  width: 100%;
  padding: 15px 0;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Card = styled.div`
  background: rgba(26, 26, 26, 0.9);
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  z-index: 10;
  margin: 10px 0;
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 10px;
  font-size: 28px;
`;

const Subtitle = styled.h2`
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: normal;
`;

const AssetTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
`;

const CategoryHeading = styled.div`
  grid-column: 1 / -1;
  color: #4fa865;
  font-weight: 600;
  font-size: 14px;
  padding: 5px 0;
  margin-top: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const AssetButton = styled.button<{ selected: boolean; isDefault?: boolean }>`
  background: ${props => {
    if (props.selected) return '#4fa865';
    if (props.isDefault) return 'rgba(79, 168, 101, 0.3)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: white;
  border: ${props => props.isDefault ? '1px solid #4fa865' : 'none'};
  border-radius: 6px;
  padding: 12px 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: ${props => {
      if (props.selected) return '#4fa865';
      if (props.isDefault) return 'rgba(79, 168, 101, 0.4)';
      return 'rgba(255, 255, 255, 0.2)';
    }};
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 10px 6px;
  }
`;

const ContinueButton = styled.button`
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 14px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;
  margin-top: 15px;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background: #1d4ed8;
  }
  
  &:disabled {
    background: #4b5563;
    cursor: not-allowed;
  }
`;

interface RegionSelectionProps {
  onRegionSelect: (regionId: string) => void;
}

const RegionSelection: React.FC<RegionSelectionProps> = ({ onRegionSelect }) => {
  const [selectedAssetType, setSelectedAssetType] = useState<string>('telecoms_sites');
  
  // Ensure the container is scrollable when content overflows on small devices
  useEffect(() => {
    const handleResize = () => {
      const appHeight = window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${appHeight}px`);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAssetTypeSelect = (assetTypeId: string) => {
    setSelectedAssetType(assetTypeId);
  };

  const handleContinue = () => {
    onRegionSelect(selectedAssetType);
  };

  return (
    <>
      <MapBackground interactive={true} />
      <Container>
        <Card>
          <Title>JPNWR Maps</Title>
          <Subtitle>Select asset type to view</Subtitle>
          
          <AssetTypeGrid>
            <CategoryHeading>Asset Types</CategoryHeading>
            {ASSET_TYPES.map((assetType) => (
              <AssetButton
                key={assetType.id}
                selected={selectedAssetType === assetType.id}
                isDefault={assetType.id === 'telecoms_sites'}
                onClick={() => handleAssetTypeSelect(assetType.id)}
              >
                {assetType.name}
              </AssetButton>
            ))}
          </AssetTypeGrid>
          
          <ContinueButton onClick={handleContinue}>
            Continue to Map
          </ContinueButton>
        </Card>
      </Container>
    </>
  );
};

export default RegionSelection; 